import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { DropdownButton } from 'react-bootstrap';
import Empty from '../../components/Empty.jsx';
import { loadingContent } from '../../components/EmptyStates.jsx';
import ReportFilters from '../../components/reports/ProjectReports/ReportFilters/index.jsx';
import ReportResults from '../../components/reports/ProjectReports/ReportResults.jsx';
import { getOrgTermsContent } from '../../content/utils.js';
import {
  selectCurrentOrganizationId,
  selectUser,
  selectUserPermissions,
} from '../../modules/auth.js';
import {
  retrieveSimplifiedEmployees,
  selectRetrieveSimplifiedEmployeesState,
} from '../../modules/employees.js';
import {
  retrieveOrganization,
  selectOrganization,
  selectRetrieveOrganizationState,
} from '../../modules/organizations.js';
import {
  retrievePortfolios,
  selectPortfolios,
} from '../../modules/portfolio.js';
import {
  retrieveBarChartData,
  retrieveProjects,
  selectProjects,
  selectReportsBarChartItems,
  selectRetrieveProjectsState,
} from '../../modules/projects.js';
import ReportsHeader from '../../components/headers/PageHeaders/Reports/ReportsHeader.jsx';
import {
  retrieveInitiativeCategories,
  selectRetrieveInitiativeCategoriesState,
} from '../../modules/initiativeCategories.js';
import { retrieveOrgStakeholders } from '../../modules/orgStakeholders.js';
import { isAuthorized } from '../../components/AuthorizedAction.js';
import ToggleBlock from '../../components/ToggleBlock.jsx';
import {
  retrieveOrgMilestones,
  selectOrgMilestones,
  selectRetrieveOrgMilestonesState,
} from '../../modules/orgMilestones.js';
import SubHeader from '../../components/SubHeader.jsx';
import ReportTypeSelect from '../../components/reports/ProjectReports/ReportTypeSelect.jsx';
import { useFetchFile } from '../../hooks/useFetchFile.js';
import { resetFilters } from '../../components/reports/ProjectReports/utils/index.js';
import {
  availableOptionsX,
  BAR_CHART_X_AXIS_TYPES,
} from '../../components/bar-chart/utils/index.js';
import DownloadReportIcon from '../../components/icons/DownloadReportIcon.jsx';
import { useDownloadPDF } from '../../hooks/useDownloadPDF.js';
import { useDownloadPNG } from '../../hooks/useDownloadPNG.js';
import {
  retrieveOrgGroups,
  selectRetrieveGroupsState,
} from '../../modules/orgGroups.js';
import {
  searchProjects,
  selectReportResult,
  selectReportResultState,
} from '../../modules/projectReports.js';
import { retrieveProjectOwners } from '../../modules/projectOwners.js';
import CustomMenuItem from '../../components/CustomMenuItem.jsx';

const mapStateToProps = (state) => {
  const retrieveSuccess = [
    selectRetrieveSimplifiedEmployeesState,
    selectRetrieveGroupsState,
    selectRetrieveOrganizationState,
    selectRetrieveProjectsState,
    selectRetrieveInitiativeCategoriesState,
    selectRetrieveOrgMilestonesState,
  ].every((select) => select(state).success);

  const { query, error, pending, success } = selectReportResultState(state);

  return {
    retrieveSuccess,
    searchQuery: query,
    searchPending: pending,
    searchSuccess: success,
    searchError: error,
    searchPerformed: !!(success || error),
    searchResults: selectReportResult(state),
    barChartData: selectReportsBarChartItems(state),
    orgMilestones: selectOrgMilestones(state),
    projects: selectProjects(state),
    portfolios: selectPortfolios(state),
    permissions: selectUserPermissions(state),
    user: selectUser(state),
    apiUrl: state.config.apiUrl,
    organizationId: selectCurrentOrganizationId(state),
    organization: selectOrganization(state),
  };
};

const mapDispatchToProps = {
  retrieveSimplifiedEmployees,
  retrieveOrganization,
  retrieveOrgGroups,
  retrieveProjects,
  retrieveInitiativeCategories,
  retrieveOrgStakeholders,
  retrievePortfolios,
  searchProjects,
  retrieveOrgMilestones,
  retrieveBarChartData,
  retrieveProjectOwners,
};

const ProjectReportsContainer = ({
  slug,
  organizationId,
  query,
  retrieveSimplifiedEmployees,
  retrieveOrganization,
  retrieveOrgGroups,
  retrieveProjects,
  retrievePortfolios,
  retrieveInitiativeCategories,
  retrieveOrgStakeholders,
  retrieveOrgMilestones,
  retrieveBarChartData,
  retrieveProjectOwners,
  barChartData,
  permissions,
  user,
  searchProjects,
  retrieveSuccess,
  searchQuery,
  searchPending,
  searchSuccess,
  searchResults,
  orgMilestones,
  projects,
  portfolios,
  apiUrl,
  organization,
}) => {
  const { loading, downloadBlob } = useFetchFile();
  const [barChartError, setBarChartError] = useState(false);
  const [reportOptions, setReportOptions] = useState({
    isVisible: false,
    reportType: 'pie',
    scaleType: 'quarter',
    timelineType: 'projects',
    showMilestones: false,
    group: BAR_CHART_X_AXIS_TYPES[0].value,
    dataset: availableOptionsX[BAR_CHART_X_AXIS_TYPES[0].value][0].value,
    percentage: false,
    hideEmpty: false,
    chartStartDate: Date.now(),
    maxGroups: Math.floor(document.body.clientWidth / 35),
    maxBars: Math.floor(document.body.clientWidth - 500),
  });

  const ganttRef = useRef(null);
  const pieRef = useRef(null);

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const { downloadPDF, loading: pdfLoading } = useDownloadPDF(
    'ganttExportContainer',
    reportOptions.scaleType
  );
  const { downloadPNG, loading: pngLoading } = useDownloadPNG(
    'ganttExportContainer',
    reportOptions.scaleType
  );

  const handleDownloadPDF = () => {
    downloadPDF();
  };

  const handleDownloadPNG = () => {
    downloadPNG();
  };

  const preSelectedFilters = {
    gantt: { archived: 'false' },
    pie: { archived: 'false' },
    bar: { archived: 'false' },
  };

  const setReportTypeDefaultQuery = () => {
    const { reportType } = reportOptions;
    resetFilters(preSelectedFilters[reportType]);
  };

  const onUpdateReportOptions = (values) => {
    setReportOptions({
      ...reportOptions,
      ...values,
    });
  };

  const onDownloadReport = () => {
    const body =
      reportOptions.reportType !== 'bar'
        ? JSON.stringify({
            chart_html: (reportOptions.reportType === 'pie' ? pieRef : ganttRef)
              .current.outerHTML,
          })
        : null;

    downloadBlob(
      apiUrl,
      `organizations/${organizationId}/projects/pdf`,
      { ...query, ...reportOptions },
      `${getOrgTermsContent('projects')}-report`,
      'pdf',
      'POST',
      body
    );
  };

  useEffect(() => {
    const checkAuthorize = isAuthorized(
      'read',
      'organizationStakeholder',
      permissions
    );

    retrieveOrganization(user.organizationId);
    retrieveOrgGroups({ active: true });
    retrieveSimplifiedEmployees({ autocomplete: false, active: true });
    retrieveProjects();
    retrievePortfolios();
    retrieveProjectOwners();
    retrieveInitiativeCategories();
    retrieveOrgMilestones();
    checkAuthorize && retrieveOrgStakeholders(true);
  }, []);

  const wasLoadingRef = useRef(false);

  useEffect(() => {
    const isLoading = pdfLoading || pngLoading || loading;
    if (wasLoadingRef.current && !isLoading) {
      setDropdownOpen(false);
    }
    wasLoadingRef.current = isLoading;
  }, [pdfLoading, pngLoading, loading]);

  useEffect(() => {
    searchProjects({ ...query, ...reportOptions }).then(() =>
      onUpdateReportOptions({ isVisible: true })
    );
  }, [query, reportOptions.group]);

  useEffect(() => {
    setReportTypeDefaultQuery();
  }, [reportOptions.reportType]);

  useEffect(() => {
    if (reportOptions.reportType === 'bar') {
      retrieveBarChartData({ ...query, ...reportOptions })
        .catch(({ error }) => setBarChartError(!!error))
        .then(({ error }) => setBarChartError(false));
    }
    // reportOptions.reportType is missing in the dependency list
    // setReportTypeDefaultQuery from the previous useEffect will change
    // query and anyway call current useEffect
  }, [
    reportOptions.group,
    reportOptions.dataset,
    reportOptions.hideEmpty,
    query,
  ]);
  return (
    <React.Fragment>
      <ReportsHeader slug={slug} />
      {retrieveSuccess ? (
        <div
          style={{
            position: 'relative',
            opacity: searchPending ? 0.5 : 1,
            cursor: searchPending ? 'wait' : 'auto',
          }}
        >
          <div style={{ pointerEvents: searchPending ? 'none' : 'auto' }}>
            <SubHeader flexJustify small>
              <h2>{getOrgTermsContent('projectReports')}</h2>

              <div className="flex">
                <ReportTypeSelect
                  reportType={reportOptions.reportType}
                  onSelect={onUpdateReportOptions}
                />
                {reportOptions.reportType !== 'bar' &&
                  reportOptions.isVisible && (
                    <div className="actions m-l-16">
                      <DropdownButton
                        noCaret
                        title=""
                        id="id"
                        className="btn-more btn btn-default"
                        open={dropdownOpen}
                        onToggle={(isOpen) => {
                          if (!isOpen && (loading || pdfLoading || pngLoading))
                            return;
                          setDropdownOpen(isOpen);
                        }}
                      >
                        <CustomMenuItem>
                          <button
                            type="button"
                            className="flex flex-align-center h-100 w-100 p-0 btn-clear bg-transparent"
                            disabled={loading}
                            onClick={onDownloadReport}
                          >
                            <DownloadReportIcon />
                            &nbsp;
                            <span className="m-l-8">
                              {loading ? 'Loading...' : 'Download Report'}
                            </span>
                          </button>
                        </CustomMenuItem>
                        <CustomMenuItem divider />
                        {reportOptions.reportType === 'gantt' && (
                          <>
                            <CustomMenuItem>
                              <button
                                type="button"
                                className="flex flex-align-center h-100 w-100 p-0 btn-clear bg-transparent"
                                disabled={pdfLoading}
                                onClick={handleDownloadPDF}
                              >
                                <DownloadReportIcon />
                                &nbsp;
                                <span className="m-l-8">
                                  {pdfLoading
                                    ? 'Loading...'
                                    : 'Download Chart PDF'}
                                </span>
                              </button>
                            </CustomMenuItem>
                            <CustomMenuItem divider />
                            <CustomMenuItem>
                              <button
                                type="button"
                                className="flex flex-align-center h-100 w-100 p-0 btn-clear bg-transparent"
                                disabled={pngLoading}
                                onClick={handleDownloadPNG}
                              >
                                <DownloadReportIcon />
                                &nbsp;
                                <span className="m-l-8">
                                  {pngLoading
                                    ? 'Loading...'
                                    : 'Download Chart PNG'}
                                </span>
                              </button>
                            </CustomMenuItem>
                          </>
                        )}
                      </DropdownButton>
                    </div>
                  )}
              </div>
            </SubHeader>

            <ToggleBlock title="Filters" showOnStart>
              <ReportFilters
                query={query}
                reportType={reportOptions.reportType}
                resetDependencies={[reportOptions.reportType]}
                permissions={permissions}
                hasParentPortfolios={organization.hasParentPortfolios}
              />
            </ToggleBlock>
            <ReportResults
              slug={slug}
              ganttRef={ganttRef}
              pieRef={pieRef}
              query={query}
              isSuccessful={searchSuccess}
              results={searchResults}
              orgMilestones={orgMilestones}
              barChartData={!barChartError ? barChartData : []}
              barChartError={barChartError}
              projects={projects}
              portfolios={portfolios}
              selectedEmployeeIds={searchQuery.employeeIds}
              selectedGroupIds={searchQuery.groupIds}
              selectedStakeholdersIds={searchQuery.organizationStakeholderIds}
              reportOptions={reportOptions}
              onUpdateReportOptions={onUpdateReportOptions}
              hasParentPortfolios={
                organization && organization.hasParentPortfolios
              }
            />
          </div>
        </div>
      ) : (
        <Empty simplified content={loadingContent} />
      )}
    </React.Fragment>
  );
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(ProjectReportsContainer);
